import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Progress } from 'antd';
import { useReportByProject } from '@/app/time-tracking/_shared/hooks';
import { PaginationControls } from '@/components/data-table/PaginationControls';
import ExpandedEntries from './ExpandedEntries';
import { getProjectColumns } from './ReportColumns';

export default function ReportByProject({
  filters,
  isAdmin = true,
  offset = 1,
  limit = 10,
  onPaginationChange,
  searchParams,
  updateParams,
}) {
  const { t } = useTranslation('forms');
  const { data: response, isLoading } = useReportByProject(filters);
  const rows = response?.data || [];
  const total = response?.total || 0;

  const maxHours = rows.length ? Math.max(...rows.map((r) => r.totalHours)) : 0;

  const baseColumns = useMemo(
    () => getProjectColumns({ searchParams, updateParams, t, isAdmin }),
    [searchParams, updateParams, t, isAdmin]
  );

  // Inject maxHours into the progress bar column render
  const columns = useMemo(
    () =>
      baseColumns.map((col) => {
        if (col.key === 'bar') {
          return {
            ...col,
            render: (_, record) => (
              <Progress
                percent={
                  maxHours
                    ? Math.round((record.totalHours / maxHours) * 100)
                    : 0
                }
                showInfo={false}
                strokeColor={record.project?.color || '#1890ff'}
                size="small"
              />
            ),
          };
        }
        return col;
      }),
    [baseColumns, maxHours]
  );

  return (
    <Table
      columns={columns}
      dataSource={rows}
      rowKey={(r) => r.project?._id}
      loading={isLoading}
      className="ant-table-content"
      pagination={
        total > limit
          ? {
              current: offset,
              pageSize: limit,
              total,
              onChange: onPaginationChange,
              showSizeChanger: false,
              showQuickJumper: false,
              position: ['bottomCenter'],
              itemRender: (page, type, originalElement) => (
                <PaginationControls
                  type={type}
                  originalElement={originalElement}
                />
              ),
            }
          : false
      }
      expandable={{
        expandedRowRender: (record) => (
          <ExpandedEntries
            filters={filters}
            groupBy="project"
            groupId={record.project?._id}
            isAdmin={isAdmin}
            showEmployee={isAdmin}
          />
        ),
      }}
    />
  );
}
